import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const ROLES = ['owner', 'admin', 'member'];
export const CHANNEL_TYPES = ['text', 'voice'];
export const roleSchema = z.enum(ROLES);
export const channelTypeSchema = z.enum(CHANNEL_TYPES);
/**
 * How the roles stand against each other.
 *
 * It lives in the shared contract, and not inside the API, because two places
 * need the very same answer: the API decides whether to allow a kick or a ban,
 * and the interface decides whether to draw the menu that asks for one. Two
 * copies of a hierarchy is how a button appears for an action the server will
 * refuse — or, worse, how they drift and the button is the one that is right.
 */
export const ROLE_RANK = { owner: 2, admin: 1, member: 0 };
/**
 * Whether `actor` may act upon `target` — strictly above, never equal.
 *
 * Equal ranks do not outrank each other, which is the whole rule for two
 * administrators: neither may remove the other, and nothing at all outranks
 * the owner.
 */
export function outranks(actor, target) {
    return ROLE_RANK[actor] > ROLE_RANK[target];
}
// ---------------------------------------------------------------------------
// Reusable field schemas
// ---------------------------------------------------------------------------
/**
 * Login identity.
 *
 * Trimmed and lowercased *before* the format is checked, so the value that
 * reaches the database is already normalized. That normalization is what makes
 * the plain unique index on `users.email` case-insensitive in practice:
 * `Ana@Exemplo.com` and `ana@exemplo.com` are the same row.
 */
export const emailSchema = z
    .string('Informe seu e-mail.')
    .trim()
    .toLowerCase()
    .pipe(z
    .email('Informe um e-mail válido.')
    .max(254, 'O e-mail pode ter no máximo 254 caracteres.'));
/**
 * Nickname and @mention — not a credential. Unique across the server, and its
 * owner may change it whenever they like.
 */
export const usernameSchema = z
    .string()
    .trim()
    .min(3, 'O nome de usuário precisa ter ao menos 3 caracteres.')
    .max(32, 'O nome de usuário pode ter no máximo 32 caracteres.')
    .regex(/^[a-z0-9._-]+$/, 'O nome de usuário aceita apenas letras minúsculas, números, ponto, hífen e sublinhado.');
export const passwordSchema = z
    .string()
    .min(8, 'A senha precisa ter ao menos 8 caracteres.')
    .max(200, 'A senha pode ter no máximo 200 caracteres.');
export const displayNameSchema = z
    .string()
    .trim()
    .min(1, 'Informe um nome de exibição.')
    .max(64, 'O nome de exibição pode ter no máximo 64 caracteres.');
export const inviteCodeSchema = z
    .string()
    .trim()
    .min(1, 'Informe o código de convite.')
    .max(64, 'Código de convite inválido.');
export const idSchema = z.uuid('Identificador inválido.');
export const guildNameSchema = z
    .string()
    .trim()
    .min(1, 'Informe o nome do servidor.')
    .max(100, 'O nome do servidor pode ter no máximo 100 caracteres.');
/** Absolute URL of the server icon; `null` clears it. */
export const guildIconUrlSchema = z
    .url('Informe uma URL de ícone válida.')
    .max(2048, 'A URL do ícone pode ter no máximo 2048 caracteres.');
export const channelNameSchema = z
    .string()
    .trim()
    .min(1, 'Informe o nome do canal.')
    .max(100, 'O nome do canal pode ter no máximo 100 caracteres.');
export const channelTopicSchema = z
    .string()
    .trim()
    .max(1024, 'O tópico pode ter no máximo 1024 caracteres.');
/**
 * Why somebody was banned, in the administrator's own words.
 *
 * Optional, and allowed to be blank: an administrator with nothing to say is
 * not made to invent something. The API stores a blank one as no reason at
 * all rather than as an empty one.
 */
export const banReasonSchema = z
    .string()
    .trim()
    .max(500, 'O motivo pode ter no máximo 500 caracteres.');
export const categoryNameSchema = z
    .string()
    .trim()
    .min(1, 'Informe o nome da categoria.')
    .max(100, 'O nome da categoria pode ter no máximo 100 caracteres.');
// ---------------------------------------------------------------------------
// Route payload schemas
// ---------------------------------------------------------------------------
/** POST /api/auth/register */
export const registerSchema = z.object({
    inviteCode: inviteCodeSchema,
    email: emailSchema,
    username: usernameSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
});
/**
 * POST /api/auth/login
 *
 * The credential is the e-mail, never the nickname: the nickname is editable,
 * and an identity that changes under its owner is not one to authenticate with.
 */
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Informe a senha.'),
});
/**
 * POST /api/auth/verify-email
 *
 * 32 random bytes in base64url; the bound is generous on purpose, since the
 * only real check is whether the digest matches a row that is still open.
 */
export const verifyEmailSchema = z.object({
    token: z
        .string('Informe o código de confirmação.')
        .trim()
        .min(1, 'Informe o código de confirmação.')
        .max(256, 'Código de confirmação inválido.'),
});
/**
 * PATCH /api/users/me — every field optional; only what is sent changes.
 * Neither of them is a credential, so changing one never touches the session.
 */
export const updateProfileSchema = z.object({
    username: usernameSchema.optional(),
    displayName: displayNameSchema.optional(),
});
/**
 * POST /api/invites
 *
 * `guildId` is required: a code opens the door to one server and to no other,
 * so which server it opens is never inferred — it is stated by whoever cuts
 * it, and the route proves they administer it.
 *
 * The other two stay optional: `{ guildId }` alone cuts the single-use code
 * that never expires, which is what the invite link in the sidebar hands out.
 */
export const createInviteSchema = z.object({
    guildId: idSchema,
    maxUses: z
        .number('Informe um número de usos.')
        .int('O número de usos precisa ser um número inteiro.')
        .min(1, 'O convite precisa permitir ao menos um uso.')
        .max(1000, 'O convite pode permitir no máximo 1000 usos.')
        .optional(),
    expiresAt: z.iso.datetime('Data de expiração inválida.').optional(),
});
/** GET /api/invites?guildId=… — the listing is always of one server's codes. */
export const listInvitesQuerySchema = z.object({
    guildId: idSchema,
});
/**
 * POST /api/guilds
 *
 * Anyone signed in with a confirmed address may create a server; the account's
 * ceiling is enforced by the API, not by this schema.
 */
export const createGuildSchema = z.object({
    name: guildNameSchema,
    iconUrl: guildIconUrlSchema.nullish(),
});
/** PATCH /api/guilds/:id — every field optional; only what is sent changes. */
export const updateGuildSchema = z.object({
    name: guildNameSchema.optional(),
    iconUrl: guildIconUrlSchema.nullish(),
});
/** POST /api/channels/:id/messages */
export const createMessageSchema = z.object({
    content: z
        .string()
        .trim()
        .min(1, 'A mensagem não pode ficar vazia.')
        .max(4000, 'A mensagem pode ter no máximo 4000 caracteres.'),
    replyToId: idSchema.nullish(),
});
/** PATCH /api/messages/:id */
export const updateMessageSchema = z.object({
    content: z
        .string()
        .trim()
        .min(1, 'A mensagem não pode ficar vazia.')
        .max(4000, 'A mensagem pode ter no máximo 4000 caracteres.'),
});
/**
 * GET /api/channels/:id/messages
 *
 * Pagination is by cursor, never by offset: `before` is the id of the oldest
 * message already on screen, so pages stay stable while new messages arrive.
 */
export const listMessagesQuerySchema = z.object({
    before: idSchema.optional(),
    limit: z.coerce
        .number('Informe um limite numérico.')
        .int('O limite precisa ser um número inteiro.')
        .min(1, 'O limite precisa ser ao menos 1.')
        .max(100, 'O limite pode ser no máximo 100.')
        .default(50),
});
/** POST /api/channels */
export const createChannelSchema = z.object({
    guildId: idSchema,
    name: channelNameSchema,
    type: channelTypeSchema,
    categoryId: idSchema.nullish(),
});
/** PATCH /api/channels/:id — every field is optional; only what is sent changes. */
export const updateChannelSchema = z.object({
    name: channelNameSchema.optional(),
    topic: channelTopicSchema.nullish(),
    categoryId: idSchema.nullish(),
});
/** POST /api/categories */
export const createCategorySchema = z.object({
    guildId: idSchema,
    name: categoryNameSchema,
});
/** PATCH /api/categories/:id — every field optional; only what is sent changes. */
export const updateCategorySchema = z.object({
    name: categoryNameSchema.optional(),
});
/**
 * PATCH /api/guilds/:id/channels/order
 *
 * The whole arrangement travels at once, in the order the sidebar shows it:
 * the index of each entry becomes its `position` and `categoryId` says which
 * group it landed in. Sending the arrangement instead of one moved channel is
 * what makes dropping a channel between two others a single request, with no
 * intermediate state where two channels claim the same position.
 */
export const reorderChannelsSchema = z.object({
    channels: z
        .array(z.object({ id: idSchema, categoryId: idSchema.nullable() }))
        .min(1, 'Informe ao menos um canal para ordenar.')
        .max(500, 'A ordenação aceita no máximo 500 canais.'),
});
/**
 * POST /api/channels/:id/attachments — the text fields of the multipart body.
 *
 * The file itself is not described here: a Zod schema validates the fields
 * around it, while the bytes are checked by the signature reader in the API.
 * The caption is what the person typed next to the file and may be empty,
 * which is why it does not borrow the floor of `createMessageSchema`.
 */
export const createAttachmentSchema = z.object({
    content: z
        .string()
        .trim()
        .max(4000, 'A mensagem pode ter no máximo 4000 caracteres.')
        .default(''),
});
/**
 * POST /api/guilds/:id/bans
 *
 * The server comes from the path, never from the body: the permission proved
 * is over the guild in the URL, and reading the target from two places is how
 * a request ends up proving one thing and writing another.
 *
 * The reason is optional and may be left blank — an administrator who wants to
 * say nothing is not made to invent something — so an empty string is stored
 * as no reason at all rather than as an empty one.
 */
export const createBanSchema = z.object({
    userId: idSchema,
    reason: banReasonSchema.optional(),
});
/** POST /api/voice/token */
export const voiceTokenSchema = z.object({
    channelId: idSchema,
});
//# sourceMappingURL=schemas.js.map